import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../Utils/Button/Button';
import LoadingIcon from '../../Utils/LoadingIcon/LoadingIcon';
import styles from './LinkedImprovementsPopup.module.css';
import { GetImprovementsByEvaluateAreaId, ImprovementDTO } from '../../../API/Improve/Improve';
import { EvaluateQuestionReference } from '../../../Types/Improve/LinkedDiagnostics';

type LinkedImprovementsPopupProps = {
  areaId?: string;
  criteriaId?: string;
};

const getQuestionId = (reference: EvaluateQuestionReference): string =>
  reference.QuestionId ?? reference.questionId ?? '';

const LinkedImprovementsPopup: React.FC<LinkedImprovementsPopupProps> = ({ areaId, criteriaId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [improvements, setImprovements] = useState<ImprovementDTO[]>([]);

  useEffect(() => {
    const loadLinkedImprovements = async () => {
      if (!areaId || !criteriaId) {
        setImprovements([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const allByArea = await GetImprovementsByEvaluateAreaId(areaId);
        const linkedToCriteria = allByArea.filter((item) => {
          const linkedEvaluateAreas = item.improvement?.linkedEvaluateAreas ?? [];
          return linkedEvaluateAreas.some(ref => getQuestionId(ref) === criteriaId);
        });

        setImprovements(linkedToCriteria);
      } catch (requestError) {
        console.error('Error loading linked improvements:', requestError);
        setError('Failed to load linked improvements. Please try again.');
        setImprovements([]);
      } finally {
        setLoading(false);
      }
    };

    loadLinkedImprovements();
  }, [areaId, criteriaId]);

  const linkedPlansCount = useMemo(() => {
    const distinctPlanIds = new Set(improvements.map(item => item.parentArea).filter(Boolean));
    return distinctPlanIds.size;
  }, [improvements]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Linked Improvements</h2>
        {improvements.length > 0 && (
          <p className={styles.subtitle}>
            {improvements.length} objective{improvements.length === 1 ? '' : 's'} across {linkedPlansCount} plan{linkedPlansCount === 1 ? '' : 's'}
          </p>
        )}
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <LoadingIcon />
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <h3>Unable to load linked improvements</h3>
            <p>{error}</p>
          </div>
        ) : improvements.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No linked improvements</h3>
            <p>This criteria is not currently linked to any improvement objectives.</p>
          </div>
        ) : (
          <div className={styles.sections}>
            {improvements.map((item, index) => {
              const objectiveTitle = item.improvement?.objective?.title || 'Untitled objective';
              return (
                <div key={`${item.parentArea}-${item.improvement?.id}-${index}`} className={styles.sectionCard}>
                  <div className={styles.cardTop}>
                    <h3 className={styles.sectionTitle}>{objectiveTitle}</h3>
                    <Button
                      size="Small"
                      colour="#FFF"
                      textColour="#000"
                      border="rgba(0,0,0,0.2)"
                      clickHandler={() => navigate(`/improve/${item.parentArea}`)}
                    >
                      Go to plan
                    </Button>
                  </div>
                  <div className={styles.breadcrumb}>
                    <span className={styles.breadcrumbSegment}>{item.parentAreaTitle || 'Improvement Plan'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedImprovementsPopup;
